/**
 * Runtime glue for the ambient rabbit: pose art plus one persistent state per
 * yard.
 *
 * State lives here rather than on the view so that a yard keeps its own timers,
 * cooldown, and rabbit position across Friends switches and trips to the map.
 * Hubert and Holly Springs never share a rabbit.
 */

import {
  createRabbitState,
  type RabbitPose,
  type RabbitState,
} from '@/world-prototype/creatures/rabbitBehavior';
import {
  PHYSICAL_SIZE_FEET,
  READABILITY,
} from '@/world-prototype/scale/worldScale';

/**
 * Poses sliced from `Rabbit-Poses-1.png` onto one canvas with a shared ground
 * baseline, so swapping `src` never shifts the rabbit's feet.
 *
 * Served as PNG on purpose. These are alpha cutouts, and lossy WebP leaves
 * halos on cutouts (the same reason `resolveRuntimeArtUrl` skips characters).
 */
// Lowercase `rabbit` on purpose: that is how the folder is tracked in git, and
// GitHub Pages serves from a case-sensitive filesystem even though macOS does not.
const RABBIT_POSE_DIR = `${import.meta.env.BASE_URL}art/animals/rabbit/poses`;

export const RABBIT_POSE_SRC: Readonly<Record<RabbitPose, string>> = {
  sit: `${RABBIT_POSE_DIR}/rabbit-sit.png`,
  alert: `${RABBIT_POSE_DIR}/rabbit-alert.png`,
  hop: `${RABBIT_POSE_DIR}/rabbit-hop.png`,
  sniff: `${RABBIT_POSE_DIR}/rabbit-sniff.png`,
  dash: `${RABBIT_POSE_DIR}/rabbit-dash.png`,
  sleep: `${RABBIT_POSE_DIR}/rabbit-sleep.png`,
};

/** Native size of every pose canvas (they are all identical). */
export const RABBIT_POSE_IMAGE = {
  imageWidth: 330,
  imageHeight: 367,
} as const;

/**
 * Real-world height the pose canvas spans, in feet.
 *
 * The tallest pose (sitting, ears up) fills 355 of the canvas's 367 px, so the
 * canvas is scaled slightly taller than the rabbit itself. Sizing this way
 * means every pose inherits correct proportions from one number, and the
 * rabbit shares the yard's perspective ramp with characters and props.
 */
export const RABBIT_CANVAS_FEET =
  PHYSICAL_SIZE_FEET.rabbit * READABILITY.rabbit * (367 / 355);

/**
 * Kid-friendly minimum tap target. The rabbit's drawn size stays small and
 * believable; only the invisible hit area grows.
 */
export const RABBIT_HIT_MIN_CSS_PX = 72;

const states = new Map<string, RabbitState>();

/** Long-lived rabbit state for a yard, created on first request. */
export function rabbitStateForYard(yardId: string): RabbitState {
  let state = states.get(yardId);
  if (!state) {
    state = createRabbitState();
    states.set(yardId, state);
  }
  return state;
}

export function resetRabbitStatesForTests(): void {
  states.clear();
}
